import { useState } from 'react';
import { Box, Card, Chip, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import {
    CreditCardOutlined as CardIcon,
    MoreVertOutlined as MoreIcon,
    StarRounded as StarIcon,
    DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { dialogOpen, dialogClose } from '@/modules/MuiDialog';
import { formatLocaleDate } from '@/utils/formatter';
import makeErrorString from '@/utils/makeErrorString';

import { useSetDefaultPaymentMethod } from '@/hooks/queries/subscription/useSetDefaultPaymentMethod';
import { useDeletePaymentMethod } from '@/hooks/queries/subscription/useDeletePaymentMethod';
import type { PaymentMethodDto } from '@/types/subscription';

import { ACCENT } from '../_shared';

interface PaymentMethodItemProps {
    method: PaymentMethodDto;
}

const PaymentMethodItem = ({ method }: PaymentMethodItemProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const setDefault = useSetDefaultPaymentMethod();
    const remove = useDeletePaymentMethod();

    const isDefault = method.isDefault === 't';

    const handleSetDefault = async () => {
        setAnchor(null);
        try {
            await setDefault.mutateAsync(method.id);
            toast.success('기본 결제 수단으로 설정했어요');
        } catch (e) {
            toast.error(makeErrorString(e, '설정에 실패했어요'));
        }
    };

    const handleRemove = () => {
        setAnchor(null);
        dialogOpen({
            title: '결제 카드를 삭제할까요?',
            message: '활성 구독에서 사용 중인 카드는 삭제할 수 없어요.',
            confirmText: '삭제',
            confirmButtonProps: { color: 'error' as any },
            onConfirmed: async () => {
                try {
                    await remove.mutateAsync(method.id);
                    toast.success('카드를 삭제했어요');
                    dialogClose();
                } catch (e) {
                    toast.error(makeErrorString(e, '삭제에 실패했어요'));
                }
            },
        });
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2,
                borderColor: isDefault ? ACCENT : 'divider',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'transform 120ms ease',
                '&:hover': { transform: 'translateY(-1px)' },
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: ACCENT,
                }}
            >
                <CardIcon />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {method.cardName ?? '카드'}
                    </Typography>
                    {method.cardLast4 && (
                        <Typography variant="caption" color="text.secondary">
                            •••• {method.cardLast4}
                        </Typography>
                    )}
                    {isDefault && (
                        <Chip
                            size="small"
                            icon={<StarIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                            label="기본"
                            sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700, height: 20 }}
                        />
                    )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {formatLocaleDate(method.createdAt)} 등록
                </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ cursor: 'pointer' }}>
                <MoreIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {!isDefault && (
                    <MenuItem onClick={handleSetDefault} disabled={setDefault.isPending} sx={{ cursor: 'pointer' }}>
                        <StarIcon fontSize="small" sx={{ mr: 1, color: ACCENT }} />
                        기본으로 설정
                    </MenuItem>
                )}
                <MenuItem onClick={handleRemove} disabled={remove.isPending} sx={{ cursor: 'pointer' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                    삭제
                </MenuItem>
            </Menu>
        </Card>
    );
};

export default PaymentMethodItem;
